import { Heading, Text, Youtube } from '@cinch-labs/design-system'
import {
  BLOK_YOUTUBE,
  StoryBlokRichText,
  getStoryblokImageServiceUrl,
  isYoutubeBlok,
} from '@cinch-nx/data-storyblok'
import {
  MARK_BOLD,
  MARK_LINK,
  NODE_HEADING,
  NODE_IMAGE,
  NODE_PARAGRAPH,
  render,
} from 'storyblok-rich-text-react-renderer'
import { TrackingEvent, useAnalytics } from '@cinch-nx/shared-analytics'

import styles from './rich-text-renderer.module.css'
import { ReactNode } from 'react'

const resolveYoutubeVideo = (props: Record<string, unknown>) =>
  isYoutubeBlok(props) ? (
    <Youtube
      {...props}
      className={styles.video}
      customThumbnail={props.thumbnail.filename}
    />
  ) : null

const convertHeadingLevelToFontSize = (level: number) => {
  switch (level) {
    case 1: {
      return 'h1'
    }
    case 2: {
      return 'h2'
    }
    case 3: {
      return 'h3'
    }
    case 4: {
      return 'h4'
    }
    case 5: {
      return 'h5'
    }
    case 6: {
      return 'h6'
    }
    default: {
      return 'h2'
    }
  }
}

const getHeadingTextAlignClassName = (textAlign: string) => {
  switch (textAlign) {
    case 'center': {
      return styles.textAlignCenter
    }
    case 'left': {
      return styles.textAlignLeft
    }
    case 'right': {
      return styles.textAlignRight
    }
    default: {
      return
    }
  }
}

interface ILinkOptions {
  openNewTab?: boolean
  adobeTracking?: TrackingEvent
}

interface IRenderWithResolverOptions {
  makeId?: boolean
  link?: ILinkOptions
}

/**
 * Renders Storyblok Rich Text body with custom options
 * @param body - Storyblok Rich Text field
 * @param options - Custom options
 *  - makeId: boolean - Adds an id to the bold text (this was already here, didn't want breaking change so left it)
 *  - link: ILinkOptions - Custom link options
 *    - openNewTab: boolean - Specify if the link should open in a new tab
 *    - adobeTracking: TrackingEvent - Specify the adobe tracking event for the link
 * @returns React.ReactNode
 */
const renderWithResolver = (
  body: StoryBlokRichText,
  options?: IRenderWithResolverOptions,
): React.ReactNode =>
  render(body, {
    nodeResolvers: {
      [NODE_IMAGE]: (_, props) => {
        const optimizedImageUrl = getStoryblokImageServiceUrl(props.src ?? '', {
          filters: [{ quality: 80 }],
        })
        return (
          <img
            {...props}
            src={optimizedImageUrl}
            style={{ height: 'auto' }}
            alt={props.alt}
          />
        )
      },
      [NODE_HEADING]: (
        children,
        props: { level: number; textAlign?: string },
      ) => {
        if (!children) return null
        return (
          <Heading
            fontSize={convertHeadingLevelToFontSize(props.level)}
            className={
              props.textAlign
                ? getHeadingTextAlignClassName(props.textAlign)
                : undefined
            }
          >
            {children}
          </Heading>
        )
      },
      [NODE_PARAGRAPH]: ((children: ReactNode, props?: { class?: string }) =>
        children ? (
          <Text className={styles.rtParagraph} style={props}>
            {children}
          </Text>
        ) : null) as (children: ReactNode) => JSX.Element | null,
    },
    blokResolvers: {
      [BLOK_YOUTUBE]: (props) => resolveYoutubeVideo(props),
    },
    markResolvers: {
      [MARK_LINK]: (children, props) => {
        const { trackEvent } = useAnalytics()
        return (
          <a
            href={props.href as string}
            target={options?.link?.openNewTab ? '_blank' : '_self'}
            rel="noopener noreferrer"
            onClick={() =>
              options?.link?.adobeTracking
                ? trackEvent(options?.link?.adobeTracking as TrackingEvent)
                : null
            }
          >
            {children}
          </a>
        )
      },
      ...(options?.makeId && {
        [MARK_BOLD]: (children) => (
          <strong id={children?.toLocaleString().replace(/\s/g, '')}>
            {children}
          </strong>
        ),
      }),
    },
  })

export default renderWithResolver
